import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Check, ShieldCheck, Banknote, ClipboardCheck, AlertTriangle,
  FileText, Clock4, Calendar, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo, ShieldMark } from "@/components/Logo";
import { SiteHeader } from "@/components/SiteHeader";
import { Seo, SITE_URL } from "@/components/Seo";

const TIMELINE = [
  { day: "Tag 1", title: "Sich beim Arbeitsamt melden", desc: "Idee dem Berater der Agentur für Arbeit mitteilen, BEVOR der ALG-I-Anspruch unter 150 Tage fällt." },
  { day: "Tag 2-5", title: "Tragfähigkeitsbescheinigung", desc: "Fachkundige Stelle (IHK/HWK/Steuerberater) prüft deinen Businessplan – ist Pflicht für den Antrag." },
  { day: "Tag 6-10", title: "Antrag einreichen", desc: "Formular + Businessplan + Lebenslauf + ggf. Sicherheitskonzept beim Sachbearbeiter der Agentur für Arbeit." },
  { day: "Tag 11+", title: "6 Monate × Pauschale + ALG I", desc: "300 € Pauschale monatlich (für Sozialversicherung) zusätzlich zu deinem ALG-I-Satz. Danach 9 weitere Monate × 300 €, wenn Aktivität nachgewiesen." },
];

const FACTS = [
  { icon: Banknote, title: "Gründungszuschuss möglich", desc: "Bei Bewilligung: 6 Monate dein ALG-I-Satz + 300 €/M. Pauschale. Optional 9 weitere Monate × 300 €." },
  { icon: ShieldCheck, title: "Tragfähigkeit ist Pflicht", desc: "Eine Fachkundige Stelle (IHK/HWK/Steuerberater) muss bescheinigen, dass dein Konzept trägt. Gründio bereitet dich darauf vor." },
  { icon: Calendar, title: "150 Tage Restanspruch", desc: "Bei Aufnahme der Selbständigkeit musst du noch mindestens 150 Tage ALG-I-Anspruch haben." },
  { icon: AlertTriangle, title: "Ermessensleistung", desc: "Der Sachbearbeiter entscheidet im Einzelfall. Eine gut vorbereitete Argumentation erhöht deine Chancen deutlich." },
];

const FAQ = [
  {
    q: "Wer hat Anspruch auf den Gründungszuschuss?",
    a: "Du musst Arbeitslosengeld I beziehen und bei Aufnahme der Selbständigkeit noch mindestens 150 Tage Restanspruch haben. Außerdem brauchst du eine Tragfähigkeitsbescheinigung von einer Fachkundigen Stelle (IHK, HWK, Steuerberater). Das ist ein offizielles Dokument mit Stempel – Gründio liefert dies NICHT, sondern bereitet dich strukturiert darauf vor.",
  },
  {
    q: "Wie hoch ist der Zuschuss?",
    a: "Bei Bewilligung in der ersten Phase (6 Monate): dein voller ALG-I-Satz plus 300 € pauschal für Sozialversicherung pro Monat. Zweite Phase (9 Monate, auf Antrag): 300 € pauschal pro Monat – wenn du eine intensive Geschäftstätigkeit nachweist. Die genauen Konditionen erfährst du bei deiner Agentur für Arbeit.",
  },
  {
    q: "Ist der Gründungszuschuss eine Pflichtleistung?",
    a: "Nein, der Zuschuss ist eine sogenannte Ermessensleistung. Der Sachbearbeiter entscheidet im Einzelfall. Eine gut vorbereitete Argumentation und ein klares Konzept erhöhen deine Chancen deutlich – genau dafür ist Gründio gedacht.",
  },
  {
    q: "Wann muss ich den Antrag stellen?",
    a: "VOR Aufnahme der Selbständigkeit. Sobald du gewerblich angemeldet bist, ist es in der Regel zu spät. Plane mindestens 4–8 Wochen Vorlauf ein, um Konzept, Fachkundige Stelle und Beratungstermin bei der Agentur für Arbeit zu koordinieren.",
  },
  {
    q: "Ersetzt Gründio die Tragfähigkeitsbescheinigung?",
    a: "Nein. Die Tragfähigkeitsbescheinigung muss von einer offiziell zugelassenen Fachkundigen Stelle (IHK, HWK, Steuerberater, Banken etc.) ausgestellt werden – mit Stempel und Unterschrift. Gründio ersetzt diese NICHT. Wir liefern dir eine strukturierte KI-Erstbewertung, mit der du optimal in dieses Gespräch gehst.",
  },
];

export default function Gruendungszuschuss() {
  const nav = useNavigate();

  const startFormFromHere = () => nav("/form?status=alg1");

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Gründungszuschuss", item: `${SITE_URL}/gruendungszuschuss` },
      ],
    },
  ];

  return (
    <div className="min-h-screen">
      <Seo
        title="Gründungszuschuss (§ 93 SGB III): Vorbereitung für ALG-I-Gründer | Gründio"
        description="Gründungszuschuss beantragen: Anspruch, Höhe, Tragfähigkeitsbescheinigung und Fahrplan. Bereite dich mit einer KI-Erstbewertung optimal auf das Gespräch mit der Fachkundigen Stelle vor."
        path="/gruendungszuschuss"
        keywords="Gründungszuschuss, § 93 SGB III, ALG I gründen, Tragfähigkeitsbescheinigung, Existenzgründung Arbeitslosigkeit, Einstiegsgeld"
        noindex
        jsonLd={jsonLd}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-60 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200/80 text-xs uppercase tracking-widest text-slate-600 mb-6 shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full brand-gradient-bg" />
              § 93 SGB III · Vorbereitung für ALG-I-Gründer
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <span className="text-slate-900">Bereite dich perfekt auf den </span>
              <span className="brand-gradient-text">offiziellen Stempel</span>
              <span className="text-slate-900"> vor.</span>
            </h1>

            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Du bist arbeitslos und willst gründen? Für deinen Antrag auf{" "}
              <b className="text-slate-900">Gründungszuschuss</b> brauchst du eine offizielle
              Tragfähigkeitsbescheinigung einer Fachkundigen Stelle (IHK, HWK, Steuerberater).
              Gründio liefert dir das <b className="text-slate-900">strukturierte
              Vorbereitungs-Briefing</b>, mit dem dein Gespräch dort sitzt – damit der
              Sachbearbeiter dein Vorhaben sofort versteht.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="alg1-start-btn"
                onClick={startFormFromHere}
                className="pulse-cta h-14 px-7 rounded-xl font-semibold shadow-lg brand-gradient-bg text-white hover:opacity-95"
              >
                KI-Score-Check starten (kostenlos)
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2 text-sm text-slate-500 px-2">
                <ShieldCheck className="h-4 w-4" style={{ color: "#10C98D" }} />
                Anonym · Keine Daten ans Amt
              </div>
            </div>

            <p className="mt-4 text-xs text-slate-500 max-w-xl leading-relaxed">
              Gründio ist eine KI-gestützte Orientierungshilfe – <b>keine</b> Rechts- oder
              Steuerberatung und <b>keine</b> Tragfähigkeitsbescheinigung im Sinne von § 93 SGB III.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-3 max-w-md">
              {[
                ["6 Monate", "+ ALG I + 300 €/M."],
                ["9 Monate", "300 €/M. Verlängerung"],
                ["150 Tage", "Restanspruch nötig"],
                ["1 Antrag", "Bevor du gründest"],
              ].map(([num, label]) => (
                <div key={num} className="rounded-2xl bg-white border border-slate-200/70 p-4 shadow-sm">
                  <div className="text-2xl font-extrabold brand-gradient-text" style={{ fontFamily: "Outfit" }}>{num}</div>
                  <div className="text-xs text-slate-600 mt-1">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: checklist preview */}
          <div className="lg:col-span-5 animate-fade-in-up">
            <div className="relative">
              <div className="absolute -inset-2 brand-gradient-bg opacity-20 blur-2xl rounded-3xl" />
              <div className="relative bg-white rounded-3xl border border-slate-200/70 p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-5">
                  <div className="text-xs uppercase tracking-widest text-slate-500">Deine Vorbereitungs-Bewertung enthält</div>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">10+ Seiten</span>
                </div>
                <ul className="space-y-3">
                  {[
                    "Strukturiertes Briefing für das Gespräch mit der Fachkundigen Stelle",
                    "Förder-Orientierung für dein Bundesland (KfW & Landesbanken)",
                    "5 typische Fragen des Sachbearbeiters der Agentur für Arbeit + Antwort-Sparring",
                    "Hinweise zu Rechtsform & DSGVO (UG vs. Einzelunternehmen)",
                    "Empfehlungen für Gründer-Geschäftskonten & akkreditierte Förderberater",
                    "Sofort-Download (perfektes Vorbereitungs-Dokument)",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-[15px] text-slate-700">
                      <span className="mt-0.5 h-5 w-5 rounded-full brand-gradient-bg flex items-center justify-center shrink-0">
                        <Check className="h-3 w-3 text-white" />
                      </span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 pt-5 border-t border-slate-200 flex items-end justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Einmalig</div>
                    <div className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>
                      29,00 €
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 text-right">
                    Als Werbungskosten<br/>steuerlich absetzbar
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facts */}
      <section className="max-w-6xl mx-auto px-6 pb-12">
        <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-slate-900" style={{ fontFamily: "Outfit" }}>
          Das musst du über den Gründungszuschuss wissen
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {FACTS.map((f) => (
            <div key={f.title} className="rounded-2xl bg-white border border-slate-200/70 p-5">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center mb-3 bg-emerald-50">
                <f.icon className="h-5 w-5" style={{ color: "#10C98D" }} />
              </div>
              <div className="font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{f.title}</div>
              <p className="text-sm text-slate-600 mt-1.5">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-slate-900" style={{ fontFamily: "Outfit" }}>
          Dein Fahrplan zur Antragstellung
        </h2>
        <div className="relative">
          <div className="absolute left-[1.1rem] top-2 bottom-2 w-0.5 bg-gradient-to-b from-emerald-400 via-emerald-200 to-amber-300" />
          <ol className="space-y-6">
            {TIMELINE.map((t, idx) => (
              <li key={t.day} className="relative pl-12">
                <div
                  className="absolute left-0 top-0 h-9 w-9 rounded-full brand-gradient-bg text-white font-bold flex items-center justify-center shadow-md"
                  style={{ fontFamily: "Outfit" }}
                >
                  {idx + 1}
                </div>
                <div className="text-xs uppercase tracking-widest text-emerald-700 mb-1">{t.day}</div>
                <div className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{t.title}</div>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{t.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA card dark */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="relative rounded-3xl overflow-hidden">
          <div className="absolute inset-0 bg-slate-900" />
          <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full opacity-30 brand-gradient-bg blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-72 w-72 rounded-full opacity-25 bg-emerald-400 blur-3xl" />
          <div className="relative p-10 text-white grid md:grid-cols-[auto,1fr,auto] gap-6 items-center">
            <div className="hidden md:block">
              <ShieldMark size={64} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2 flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4" /> Schritt 1: KI-Erstbewertung
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>
                Erst kostenlos das Express-Feedback holen.
              </h2>
              <p className="text-white/70 mt-3 max-w-2xl">
                In 2 Minuten zeigt dir die KI, an welchen Stellen du dein Konzept noch schärfen
                solltest, bevor du zur Fachkundigen Stelle gehst.
                Dein Status &bdquo;Arbeitslos (ALG I)&ldquo; ist bereits vorausgewählt.
              </p>
            </div>
            <Button
              data-testid="alg1-bottom-btn"
              onClick={startFormFromHere}
              className="h-14 px-7 rounded-xl font-semibold brand-gradient-bg text-white hover:opacity-95"
            >
              <Sparkles className="h-5 w-5 mr-2" /> Jetzt starten
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ for SEO */}
      <section className="max-w-3xl mx-auto px-6 pb-24">
        <h2 className="text-2xl font-bold mb-6 text-slate-900" style={{ fontFamily: "Outfit" }}>
          Häufige Fragen zum Gründungszuschuss
        </h2>
        <div className="space-y-3">
          {FAQ.map((f) => (
            <details key={f.q} className="group bg-white rounded-2xl border border-slate-200/70 p-5">
              <summary className="cursor-pointer font-semibold text-slate-900 list-none flex justify-between items-center">
                <span style={{ fontFamily: "Outfit" }}>{f.q}</span>
                <span className="text-emerald-600 group-open:rotate-45 transition-transform">＋</span>
              </summary>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-200/70 bg-white/60">
        <div className="max-w-6xl mx-auto px-6 py-8 text-xs text-slate-500 grid sm:grid-cols-2 gap-4 items-start">
          <div className="space-y-2">
            <Logo size="sm" />
            <p className="max-w-md leading-relaxed">
              Gründio bietet eine automatisierte Orientierungshilfe und KI-gestützte
              Erstbewertung. <b>Keine Rechts- oder Steuerberatung</b>.
              <b> Keine Tragfähigkeitsbescheinigung</b> nach § 93 SGB III.
            </p>
          </div>
          <div className="sm:text-right">
            © {new Date().getFullYear()} Gründio · Stand 2026
          </div>
        </div>
      </footer>
    </div>
  );
}
